import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, ChevronDown, ChevronUp } from "lucide-react";

/**
 * VideoIntroModal — WCAG 2.2 AA accessible video modal.
 * - DialogTitle + DialogDescription (screen reader).
 * - <video controls> + <track kind="captions"> ile Türkçe altyazı.
 * - Autoplay YOK — kullanıcı yayınlatmadan başlamaz.
 * - Genişleyebilir metin transkripti (Türkçe).
 * - Esc kapatır, focus modal içinde kalır (Radix default).
 */
export default function VideoIntroModal({ open, onOpenChange }) {
  const [showTranscript, setShowTranscript] = useState(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden bg-background"
        data-testid="video-intro-modal"
        aria-labelledby="video-intro-title"
        aria-describedby="video-intro-desc"
      >
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle id="video-intro-title">Leranio'yu 60 saniyede keşfet</DialogTitle>
          <DialogDescription id="video-intro-desc">
            Öğretmen, öğrenci ve veli için AI destekli okul yönetimi turu. Altyazılı ve transkriptli.
          </DialogDescription>
        </DialogHeader>

        <div className="relative bg-black/95 aspect-video">
          {/* Placeholder poster — gerçek video yayınlanınca src eklenecek */}
          <video
            data-testid="video-intro-player"
            controls
            preload="metadata"
            className="w-full h-full"
            poster="/logo.png"
            aria-describedby="video-intro-desc"
          >
            <source src="/videos/leranio-intro.mp4" type="video/mp4" />
            <track
              kind="captions"
              srcLang="tr"
              label="Türkçe Altyazı"
              src="/videos/leranio-intro.tr.vtt"
              default
            />
            Tarayıcınız video etiketini desteklemiyor. Aşağıdaki transkripti okuyabilirsiniz.
          </video>
        </div>

        <div className="px-6 py-4 border-t border-border bg-card/50">
          <button
            type="button"
            onClick={() => setShowTranscript((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            data-testid="transcript-toggle"
            aria-expanded={showTranscript}
            aria-controls="video-transcript-body"
          >
            {showTranscript ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
            Yazılı Transkript {showTranscript ? "(gizle)" : "(göster)"}
          </button>
          {showTranscript && (
            <div id="video-transcript-body" className="mt-3 text-sm leading-relaxed space-y-2 text-muted-foreground max-h-64 overflow-y-auto" data-testid="video-transcript">
              <p><strong>[0:00]</strong> Merhaba! Leranio, okulunuzun tüm dijital ihtiyaçlarını tek bir yerde toplayan yapay zekâ destekli platform.</p>
              <p><strong>[0:10]</strong> Öğretmenler ödev ve sınavlarını AI ile saniyeler içinde oluşturur, sınıfa gönderir.</p>
              <p><strong>[0:20]</strong> Öğrenciler AI Öğretmen ile Sokratik yöntem kullanarak kendi çözümüne ulaşır, meydan okumalarla seviyelerine uygun quiz'ler çözer.</p>
              <p><strong>[0:30]</strong> Veliler çocuklarının ilerlemesini gerçek zamanlı takip eder, öğretmenlerle mesajlaşır, görüşme randevusu alır.</p>
              <p><strong>[0:40]</strong> Yöneticiler tek panelden okulun tümünü yönetir; KVKK uyumlu raporlar, denetim kayıtları ve güvenli veri saklama ile.</p>
              <p><strong>[0:50]</strong> Adaptif zorluk, konu boşluğu tespiti ve otomatik çalışma planlarıyla her öğrenci ilerler.</p>
              <p><strong>[0:60]</strong> Hemen ücretsiz demo hesabıyla dene. Leranio — Learning Made Intelligent.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
